import { useState } from "react";

import { $enum } from "ts-enum-util";

import { PlanningDay } from "@eisox/api/core-api";
import { ActionButton, Button, Dialog, Field, Select } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { ArrowRightIcon, CalendarIcon, CopyIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useSelector } from "@tanstack/react-form";

import type { CopyToDaysSchemaType } from "~/features/planning";
import { TargetDays, copyToDaysSchema, targetDaysToDays } from "~/features/planning";
import { m } from "~/paraglide/messages";

interface CopyPlanningDialogProps extends Dialog.RootProps {
  onCopy?: (data: CopyToDaysSchemaType) => Promise<void>;
  disabled?: boolean;
}

const CopyPlanningDialog: React.FC<CopyPlanningDialogProps> = ({
  onCopy,
  disabled,
  open: openProp,
  // eslint-disable-next-line @typescript-eslint/unbound-method
  onOpenChange,
  defaultOpen = false,
  ...props
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen,
  });
  const [targetDays, setTargetDays] = useState<TargetDays>(TargetDays.MondayToFriday);

  const defaultValues = {
    mode: "by-day" as const,
    dayToCopy: PlanningDay.MONDAY,
    targetDays: targetDaysToDays[TargetDays.MondayToFriday],
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: ({ value }) => {
        const result = copyToDaysSchema.safeParse(value);
        if (!result.success) return result.error;
      },
    },
    onSubmit: async ({ value }) => {
      await onCopy?.(value);
      handleOpenChange(false);
    },
  });

  const dayToCopy = useSelector(form.store, state => state.values.dayToCopy);

  const daysOptions = $enum(PlanningDay)
    .getValues()
    .map(day => ({ name: m[day](), value: day }));

  const targetDaysOptions = [
    { name: `${m.monday()} - ${m.friday()}`, value: TargetDays.MondayToFriday },
    { name: `${m.saturday()} - ${m.sunday()}`, value: TargetDays.SaturdayToSunday },
    { name: `${m.monday()} - ${m.sunday()}`, value: TargetDays.MondayToSunday },
    { name: m.personalized(), value: TargetDays.Custom },
  ];

  const handleOpenChange = (open: boolean) => {
    form.reset(defaultValues);
    setTargetDays(TargetDays.MondayToFriday);
    setOpen(open);
  };

  const handleChangeTargetDays = (value: string | string[]) => {
    setTargetDays(value as TargetDays);
    form.setFieldValue("targetDays", targetDaysToDays[value as TargetDays]);
  };

  return (
    <Dialog.Root {...props} open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        <Button disabled={disabled}>
          <CopyIcon />
        </Button>
      </Dialog.Trigger>
      <Dialog.Content title={m.copy_day_to_another_days()} icon={<CalendarIcon />}>
        <form
          className="md:w-117.5 flex w-full flex-col text-base"
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <div className="m-2.5 gap-2.5 flex flex-col">
              <form.AppField name="dayToCopy">
                {field => (
                  <field.Select
                    label={m.day_to_duplicate()}
                    classNames={{ control: "w-full" }}
                    multiple={false}
                    options={daysOptions}
                    searchInput={false}
                  />
                )}
              </form.AppField>
              <Field.Root className="w-full">
                <Field.Label>{m.to()}</Field.Label>
                <div className="gap-2.5 *:min-w-0 flex w-full *:flex-1">
                  <Select.Root multiple={false} value={targetDays} onValueChange={handleChangeTargetDays}>
                    <Select.Trigger className="w-full">
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Content>
                      <Select.Viewport>
                        {targetDaysOptions.map(option => (
                          <Select.Item key={option.value} value={option.value}>
                            {option.name}
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Root>
                  {targetDays === TargetDays.Custom && (
                    <form.AppField name="targetDays">
                      {field => (
                        <field.Select
                          classNames={{ control: "w-full" }}
                          multiple
                          placeholder={m.select_days()}
                          searchInput={false}
                          options={daysOptions.filter(day => day.value !== dayToCopy)}
                        />
                      )}
                    </form.AppField>
                  )}
                </div>
              </Field.Root>
            </div>
            <div className="mt-10 gap-5 flex items-center self-center">
              <Dialog.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Dialog.Close>
              <ActionButton type="submit">
                {m.duplicate()} <ArrowRightIcon />
              </ActionButton>
            </div>
          </form.AppForm>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

CopyPlanningDialog.displayName = "CopyPlanningDialog";

export { CopyPlanningDialog };
export type { CopyPlanningDialogProps };
